// src/page/admin/scraping/TarifsLegalin.tsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Tabs, 
  Table, 
  Tag, 
  Spin, 
  Empty, 
  Space, 
  Alert,
  Timeline,
  Statistic,
  Row,
  Col
} from 'antd';
import { 
  ReloadOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined,
  HistoryOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/fr';

moment.locale('fr');

const { TabPane } = Tabs;

interface TarifType {
  type: string;
  prix: string[];
}

interface TableauTarif {
  tableau_index: number;
  headers: string[] | null;
  rows: string[][];
}

interface InformationGenerale {
  texte: string;
  prix_trouves: string[];
}

interface TarifsData {
  types_annonce: TarifType[];
  tarifs_forfaitaires: TableauTarif[];
  tarifs_caractere: string[];
  informations_generales: InformationGenerale[];
}

interface ApiResponse {
  success: boolean;
  data: TarifsData;
  metadata?: {
    source: 'cache' | 'scraping';
    cachedAt?: string;
    scrapedAt?: string;
    expiresAt?: string;
    itemsFound?: {
      types_annonce: number;
      tableaux: number;
      informations: number;
    };
  };
}

const TarifsLegalin: React.FC = () => {
  const [tarifs, setTarifs] = useState<TarifsData | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  const loadTarifs = async () => {
    setLoading(true);
    setError(null);

    console.log('📡 [TarifsLegalin] Chargement des données...');

    try {
      const response = await axios.get<ApiResponse>('/api_alo/scraper/tarifs-legalin');

      console.log('✅ [TarifsLegalin] Réponse reçue:', response.data);

      if (response.data.success) {
        setTarifs(response.data.data);
        setMetadata(response.data.metadata);
        console.log('📊 [TarifsLegalin] Données chargées:', {
          types: response.data.data.types_annonce.length,
          tableaux: response.data.data.tarifs_forfaitaires.length,
          infos: response.data.data.informations_generales.length
        });
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (err: any) {
      console.error('❌ [TarifsLegalin] Erreur:', err);
      
      if (err.response) {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || err.message}`);
      } else if (err.request) {
        setError('Pas de réponse du serveur. Vérifiez que le backend tourne sur le port 5200.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTarifs = async () => {
    setRefreshing(true);
    setError(null);

    console.log('🔄 [TarifsLegalin] Rafraîchissement forcé...');

    try {
      const response = await axios.get<ApiResponse>('/api_alo/scraper/tarifs-legalin/refresh');

      console.log('✅ [TarifsLegalin] Rafraîchissement terminé');

      if (response.data.success) {
        setTarifs(response.data.data);
        setMetadata(response.data.metadata);
      }
    } catch (err: any) {
      console.error('❌ [TarifsLegalin] Erreur refresh:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTarifs();
  }, []);

  // Colonnes pour le tableau des types d'annonce
  const typesColumns = [
    {
      title: 'Type d\'annonce',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Prix',
      dataIndex: 'prix',
      key: 'prix',
      render: (prix: string[]) => (
        <Space>
          {prix.map((p, idx) => (
            <Tag color="blue" key={idx}>{p}</Tag>
          ))}
        </Space>
      )
    }
  ];

  const renderTableauTarifs = (tableau: TableauTarif, index: number) => {
    if (!tableau.rows || tableau.rows.length === 0) return null;

    const columns = tableau.headers && tableau.headers.length > 0
      ? tableau.headers.map((header, idx) => ({
          title: header,
          dataIndex: `col${idx}`,
          key: `col${idx}`,
          render: (text: string) => text || '-'
        }))
      : tableau.rows[0].map((_, idx) => ({
          title: `Colonne ${idx + 1}`,
          dataIndex: `col${idx}`,
          key: `col${idx}`
        }));

    const dataSource = tableau.rows.map((row, rowIdx) => {
      const rowData: any = { key: rowIdx };
      row.forEach((cell, cellIdx) => {
        rowData[`col${cellIdx}`] = cell;
      });
      return rowData;
    });

    return (
      <Card 
        key={index} 
        title={`Tableau ${tableau.tableau_index}`}
        style={{ marginBottom: 16 }}
        size="small"
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          size="small"
        />
      </Card>
    );
  };

  if (loading && !tarifs) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Chargement des tarifs..." />
        <p style={{ marginTop: 16, color: '#888' }}>
          Première connexion, veuillez patienter...
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* En-tête */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>
              <DatabaseOutlined /> Tarifs Legalin.fr
            </h2>
            <p style={{ color: '#888', margin: '8px 0 0 0' }}>
              Données scrapées depuis le site Legalin.fr pour référence
            </p>
          </Col>
          {/* <Col>
            <Button
              type="primary"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={refreshTarifs}
              loading={refreshing}
              size="large"
            >
              Actualiser
            </Button>
          </Col> */}
        </Row>
      </Card>

      {/* Métadonnées */}
      {metadata && (
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Source"
                value={metadata.source === 'cache' ? 'Cache' : 'Scraping'}
                prefix={metadata.source === 'cache' ? <DatabaseOutlined /> : <CloudDownloadOutlined />}
                valueStyle={{ color: metadata.source === 'cache' ? '#3f8600' : '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Dernière mise à jour"
                value={metadata.scrapedAt ? moment(metadata.scrapedAt).fromNow() : metadata.cachedAt ? moment(metadata.cachedAt).fromNow() : 'N/A'}
                prefix={<ClockCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Expire"
                value={metadata.expiresAt ? moment(metadata.expiresAt).fromNow() : 'N/A'}
                prefix={<InfoCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Tableaux trouvés"
                value={tarifs?.tarifs_forfaitaires.length || 0}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Erreur */}
      {error && (
        <Alert
          message="Erreur"
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Contenu principal */}
      {tarifs ? (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Onglet Types d'annonce */}
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined />
                Types d'annonce ({tarifs.types_annonce.length})
              </span>
            }
            key="1"
          >
            <Card>
              {tarifs.types_annonce.length > 0 ? (
                <Table
                  columns={typesColumns}
                  dataSource={tarifs.types_annonce.map((item, idx) => ({
                    ...item,
                    key: idx
                  }))}
                  pagination={{ pageSize: 10 }}
                />
              ) : (
                <Empty description="Aucun type d'annonce trouvé" />
              )}
            </Card>
          </TabPane>

          {/* Onglet Tarifs forfaitaires */}
          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                Tarifs forfaitaires ({tarifs.tarifs_forfaitaires.length})
              </span>
            }
            key="2"
          >
            {tarifs.tarifs_forfaitaires.length > 0 ? (
              tarifs.tarifs_forfaitaires.map((tableau, idx) => 
                renderTableauTarifs(tableau, idx)
              )
            ) : (
              <Empty description="Aucun tableau de tarifs trouvé" />
            )}
          </TabPane>

          {/* Onglet Informations générales */}
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined />
                Informations ({tarifs.informations_generales.length})
              </span>
            }
            key="3"
          >
            <Card>
              {tarifs.informations_generales.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {tarifs.informations_generales.map((info, idx) => (
                    <Alert
                      key={idx}
                      message={info.texte}
                      type="info"
                      showIcon
                      description={
                        <Space>
                          <strong>Prix trouvés:</strong>
                          {info.prix_trouves.map((prix, pIdx) => (
                            <Tag color="green" key={pIdx}>{prix}</Tag>
                          ))}
                        </Space>
                      }
                    />
                  ))}
                </Space>
              ) : (
                <Empty description="Aucune information trouvée" />
              )}
            </Card>
          </TabPane>

          {/* Onglet Tarifs au caractère */}
          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                Tarifs au caractère ({tarifs.tarifs_caractere.length})
              </span>
            }
            key="4"
          >
            <Card>
              {tarifs.tarifs_caractere.length > 0 ? (
                <Space wrap>
                  {tarifs.tarifs_caractere.map((tarif, idx) => (
                    <Tag color="blue" key={idx}>{tarif}</Tag>
                  ))}
                </Space>
              ) : (
                <Empty description="Aucun tarif au caractère trouvé" />
              )}
            </Card>
          </TabPane>
        </Tabs>
      ) : (
        <Card>
          <Empty 
            description="Aucune donnée disponible" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button 
              type="primary" 
              onClick={loadTarifs}
            >
              Charger les données
            </Button>
          </Empty>
        </Card>
      )}
    </div>
  );
};

export default TarifsLegalin;